import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/hooks/useAuthContext";
import { Building2, Lock, Mail, WifiOff } from "lucide-react";

type NoOfficeScreenProps = {
  /** When set, show connector-denied copy instead of «not linked to office». */
  mode?: "no_office" | "connector_denied";
};

export const NoOfficeScreen = ({ mode = "no_office" }: NoOfficeScreenProps) => {
  const { logout, user, backendReachable, connectorDenied } = useAuthContext();

  // Backend unreachable — connectivity issue, not missing office
  if (!backendReachable) {
    return (
      <div className="flex items-center justify-center h-screen bg-background" dir="rtl">
        <div className="max-w-lg text-center space-y-6 p-8">
          <div className="mx-auto w-20 h-16 rounded-full bg-red-50 flex items-center justify-center">
            <WifiOff className="h-8 w-8 text-red-500" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">שגיאת תקשורת</h1>
            <p className="text-muted-foreground">
              לא ניתן להתחבר לשרת. אנא נסה שוב מאוחר יותר.
            </p>
          </div>

          <div className="space-y-3">
            <Button onClick={() => window.location.reload()}>
              נסה שוב
            </Button>
            <div>
              <Button variant="outline" onClick={() => logout()}>
                התנתק
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const showConnectorDenied =
    mode === "connector_denied" || connectorDenied != null;

  if (showConnectorDenied) {
    return (
      <div className="flex items-center justify-center h-screen bg-background" dir="rtl">
        <div className="max-w-lg text-center space-y-6 p-8">
          <div className="mx-auto w-20 h-16 rounded-full bg-indigo-50 flex items-center justify-center">
            <Lock className="h-8 w-8 text-indigo-600" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              הערוץ אינו כלול בתוכנית
            </h1>
            <p className="text-muted-foreground">
              התוכנית שלך אינה כוללת את ערוץ ה־Web app (webapp-connector).
              <br />
              פנה לתמיכה או שדרג את התוכנית כדי להשתמש באפליקציית האינטרנט.
            </p>
            {user?.email ? (
              <p className="text-sm text-muted-foreground pt-1">{user.email}</p>
            ) : null}
          </div>

          <div className="space-y-3">
            <a
              href="mailto:support@legalix.ai?subject=בקשה לגישת Web app"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <Mail className="h-4 w-4" />
              support@legalix.ai
            </a>

            <div>
              <Button variant="outline" onClick={() => logout()}>
                התנתק
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Backend reachable but user has no office
  return (
    <div className="flex items-center justify-center h-screen bg-background" dir="rtl">
      <div className="max-w-lg text-center space-y-6 p-8">
        <div className="mx-auto w-20 h-16 rounded-full bg-muted flex items-center justify-center">
          <Building2 className="h-8 w-8 text-muted-foreground" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">אינך משוייך למשרד</h1>
          <p className="text-muted-foreground">
            החשבון שלך ({user?.email}) עדיין לא משויך למשרד.
            <br />
            פנה למנהל המערכת כדי לקבל הזמנה.
          </p>
        </div>

        <div className="space-y-3">
          <a
            href="mailto:support@legalix.ai?subject=בקשה להצטרפות למשרד"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <Mail className="h-4 w-4" />
            support@legalix.ai
          </a>

          <div>
            <Button variant="outline" onClick={() => logout()}>
              התנתק
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
